"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { MapPin, Stethoscope, Search, ShieldCheck, TrendingUp, FileText, CircleCheck as CheckCircle2, ArrowRight, UserCheck } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
const strategyPillars = [{
  id: "pillar-1",
  step: "Phase 01",
  title: "Google Business Profile & Map Pack Authority",
  icon: MapPin,
  description: "I restructure and verify your primary practice profiles, optimize medical service categories, embed verified dermatologist schema, and build precise local citations across accredited healthcare directories.",
  deliverables: ["Medical specialty category alignment", "NAP (Name, Address, Phone) consistency audit", "Local service area optimization without spam tactics"]
}, {
  id: "pillar-2",
  step: "Phase 02",
  title: "Treatment-Specific High-Intent Keyword Targeting",
  icon: Search,
  description: "Patients rarely search generally; they search for exact solutions like 'mole mapping near me', 'acne scar laser clinic', or 'private dermatologist consultation'. I build content around qualified intent.",
  deliverables: ["Symptom and treatment keyword mapping", "Zero-volume and high-intent query capture", "Local search competitor gap analysis"]
}, {
  id: "pillar-3",
  step: "Phase 03",
  title: "Clinical E-E-A-T & Medical Schema Architecture",
  icon: ShieldCheck,
  description: "Dermatology sits strictly under Google's 'Your Money or Your Life' (YMYL) standards. I audit medical authorship, GMC registrations, credentials, and structured JSON-LD data to signal clinical credibility.",
  deliverables: ["MedicalWebPage and Physician JSON-LD markup", "Clinician bio optimization and credential validation", "Patient safety policy and review architecture"]
}, {
  id: "pillar-4",
  step: "Phase 04",
  title: "Geo-Targeted Conversion Landing Pages",
  icon: TrendingUp,
  description: "Every page is structured to turn local searchers into booked consultations. I streamline booking paths, display clinic travel distance, and eliminate technical friction on mobile devices.",
  deliverables: ["Location-specific treatment landing pages", "Direct consultation booking form integration", "Core Web Vitals and mobile page speed tuning"]
}];
const clinicalFaqs = [{
  question: "How do you navigate Google's strict medical (YMYL) guidelines?",
  answer: "I apply Google's Search Quality Rater standards directly to your clinic's pages. Every treatment page is authored or reviewed by qualified dermatologists, backed by MedicalEntity and Physician schema, and cross-referenced with official professional bodies."
}, {
  question: "Why focus on specific treatments instead of generic 'dermatologist' terms?",
  answer: "Generic keywords bring high bounce rates and broad questions. Patients searching for specific procedures—such as eczema patch testing or cosmetic lesion removal—have higher booking intent and shorter consultation cycles."
}, {
  question: "Will I work directly with you or an account manager?",
  answer: "You work directly with me on all strategy, technical auditing, and local search implementation."
}];
export default function LocalStrategy() {
  const shouldReduceMotion = useReducedMotion();
  return <section id="localdermatologystrategy" data-nav="light" className="bg-background text-foreground py-20 md:py-28 relative overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Header Block */}
        <div className="max-w-3xl mx-auto text-center mb-16 md:mb-20">
          <motion.div initial={{
          opacity: 0,
          y: shouldReduceMotion ? 0 : 16
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          ease: "easeOut"
        }} className="flex items-center justify-center gap-2 mb-4">
            <Badge variant="outline" className="px-3 py-1 text-xs uppercase tracking-wide border-border">
              <Stethoscope className="size-3.5 text-primary" />
              <span>Dermatology Practice Growth</span>
            </Badge>
          </motion.div>

          <motion.h2 initial={{
          opacity: 0,
          y: shouldReduceMotion ? 0 : 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.55,
          delay: 0.1,
          ease: "easeOut"
        }} className="text-2xl sm:text-3xl lg:text-4xl font-default font-bold text-foreground tracking-tight leading-tight">
            A Clear Strategy to Drive Local Patients to Your Dermatology Clinic
          </motion.h2>

          <motion.p initial={{
          opacity: 0,
          y: shouldReduceMotion ? 0 : 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.55,
          delay: 0.2,
          ease: "easeOut"
        }} className="mt-6 text-base sm:text-lg text-muted-foreground leading-relaxed">
            I design and implement hands-on local SEO systems tailored for private skin clinics and dermatologists. 
            By aligning medical authority with high-intent localized search queries, your practice ranks where prospective patients actively search for treatment.
          </motion.p>
        </div>

        {/* 4 Pillars Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8 mb-16">
          {strategyPillars.map((pillar, index) => {
          const Icon = pillar.icon;
          return <motion.div key={pillar.id} data-index={index} initial={{
            opacity: 0,
            y: shouldReduceMotion ? 0 : 24
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: index * 0.1,
            ease: "easeOut"
          }}>
                <Card className="h-full border border-border shadow-sm hover:shadow-md transition-[border-color,box-shadow] duration-300 bg-card">
                  <CardHeader className="pb-4">
                    <div className="flex items-center justify-between gap-4 mb-2">
                      <div className="size-11 rounded-lg bg-secondary flex items-center justify-center text-primary">
                        <Icon className="size-5 text-primary" />
                      </div>
                      <span className="text-xs font-semibold text-muted-foreground tracking-wider uppercase">
                        {pillar.step}
                      </span>
                    </div>
                    <CardTitle className="text-xl font-default font-semibold text-card-foreground">
                      {pillar.title}
                    </CardTitle>
                    <CardDescription className="text-muted-foreground text-sm leading-relaxed mt-2">
                      {pillar.description}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="pt-2">
                    <div className="border-t border-border pt-4">
                      <p className="text-xs font-medium text-foreground uppercase tracking-wider mb-3">
                        Key Implementation Focus:
                      </p>
                      <ul className="space-y-2.5">
                        {pillar.deliverables.map((item, itemIdx) => <li key={itemIdx} data-index={itemIdx} className="flex items-start gap-2.5 text-sm text-muted-foreground">
                            <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                            <span>{item}</span>
                          </li>)}
                      </ul>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>;
        })}
        </div>

        {/* Transparency Box & Clinical FAQ */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start mb-16">
          {/* Left Column: Direct Delivery Guarantee */}
          <motion.div initial={{
          opacity: 0,
          x: shouldReduceMotion ? 0 : -20
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.55,
          ease: "easeOut"
        }} className="lg:col-span-5 bg-secondary/50 rounded-xl p-6 sm:p-8 border border-border flex flex-col justify-between h-full">
            <div>
              <div className="flex items-center gap-3 mb-5">
                <div className="size-10 rounded-lg bg-primary text-primary-foreground flex items-center justify-center">
                  <UserCheck className="size-5" />
                </div>
                <div>
                  <h3 className="font-default font-semibold text-foreground text-lg">
                    Direct Independent SEO Delivery
                  </h3>
                  <p className="text-xs text-muted-foreground">
                    Yassine Essaadi · SEO Consultant
                  </p>
                </div>
              </div>

              <p className="text-sm text-muted-foreground leading-relaxed mb-6">
                Unlike general marketing agencies that recycle generic templates, I personally build and execute the search strategy for your dermatology practice. You get clear monthly deliverables, honest search data, and zero outsourced guesswork.
              </p>

              <div className="space-y-3 pt-2">
                <div className="flex items-center gap-2.5 text-xs text-foreground font-medium">
                  <FileText className="size-4 text-primary" />
                  <span>Transparent search visibility and booking reports</span>
                </div>
                <div className="flex items-center gap-2.5 text-xs text-foreground font-medium">
                  <CheckCircle2 className="size-4 text-primary" />
                  <span>No lock-in retainer contracts or hidden setup fees</span>
                </div>
              </div>
            </div>

            <div className="mt-8 pt-6 border-t border-border">
              <Button asChild className="w-full justify-center" size="lg">
                <Link to="/ContactPage">
                  <span>Book a Dermatology SEO Consultation</span>
                  <ArrowRight className="size-4" />
                </Link>
              </Button>
            </div>
          </motion.div>

          {/* Right Column: FAQ Accordion */}
          <motion.div initial={{
          opacity: 0,
          x: shouldReduceMotion ? 0 : 20
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.55,
          ease: "easeOut"
        }} className="lg:col-span-7 rounded-xl border border-border bg-card p-6 sm:p-8 shadow-xs">
            <div className="mb-6">
              <Badge variant="secondary" className="mb-2">Methodology FAQ</Badge>
              <h3 className="text-xl font-default font-semibold text-card-foreground">
                How I Execute Dermatology SEO
              </h3>
              <p className="text-sm text-muted-foreground mt-1">
                Practical answers on local search, compliance, and clinical patient acquisition.
              </p>
            </div>

            <Accordion type="single" collapsible defaultValue="item-0" className="w-full">
              {clinicalFaqs.map((faq, index) => <AccordionItem key={index} value={`item-${index}`} className="border-border">
                  <AccordionTrigger className="text-sm sm:text-base font-default font-medium text-foreground hover:no-underline">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-sm text-muted-foreground leading-relaxed">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>)}
            </Accordion>
          </motion.div>
        </div>

        {/* Footer Navigation Bar */}
        <div className="border-t border-border pt-8 flex flex-col sm:flex-row items-center justify-between gap-4 text-center sm:text-left">
          <p className="text-sm text-muted-foreground">
            Looking for location-specific search consultancy across the UK?
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3">
            <Button asChild variant="outline" size="sm">
              <Link to="/LocalSEOPage">
                <span>View All Local SEO Services</span>
              </Link>
            </Button>
            <Button asChild variant="ghost" size="sm">
              <Link to="/CaseStudiesPage">
                <span>Explore SEO Case Studies</span>
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}